/**
 * Directory Component
 * The primary organization browsing page. Offers two view modes:
 * 1. Card View -- paginated grid of organization cards with sort, search, and filters
 * 2. Network Graph -- interactive force-directed graph (desktop) or stats list (mobile)
 *
 * Key behaviors:
 * - URL search params are the source of truth for view mode, filters, and sort order.
 *   Changing a filter updates both local state and the URL, enabling shareable links.
 * - On mobile (< 992px), defaults to card view since the graph requires a larger viewport.
 * - Fetches organizations server-side with pagination; search uses a separate endpoint
 *   when the query is >= 2 characters.
 * - Automatically resets to page 1 whenever filters, search, or sort change.
 * - Admin users see an additional "Profile Completeness" filter (hasDescription).
 * - Publishes page context to the floating chat widget via useChatPageEffect.
 * - Redirects stale URLs (view=ecosystem, view=partners, view=mentors) to new dedicated pages
 *   (Directory, Ecosystem, Mentors, Partners).
 */
import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Card,
    Badge,
    Spinner,
    Pagination,
    Nav,
    Collapse,
} from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import ShareInviteButton from '@/components/common/ShareInviteButton';
import { useOrganizationApi } from '@/shared/services/organizationApi';
import { useMembership } from '@/shared/contexts/MembershipContext';
import { EcosystemGraph, GraphMobileView } from '@/features/graph';
import { SearchAutocomplete } from '@/components/SearchAutocomplete';
import {
    highlightText,
    highlightAndTruncate,
} from '@/shared/utils/highlightText';
import useChatPageEffect from '@/shared/hooks/useChatPageEffect';
import useEcosystemTaxonomy from '@/shared/hooks/useEcosystemTaxonomy';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import useCapability from '@/shared/hooks/useCapability';
import { RILE_MARK_URL } from '@/shared/utils/brandAssets';

import './Directory.css';

// Number of organization cards per page in card view
const ITEMS_PER_PAGE = 12;

// Bootstrap 'lg' breakpoint check -- graph view is desktop-only
const isMobile = () => window.innerWidth < 992;

export default function Directory() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { role: membershipRole, isMember } = useMembership();
    const {
        sectors: taxonomySectors,
        regions: taxonomyRegions,
        programmingTypes: taxonomyProgrammingTypes,
    } = useEcosystemTaxonomy();
    const { ecosystem } = useEcosystem();
    const ecosystemName = ecosystem?.name || 'the ecosystem';
    // The Network Graph view depends on the per-tenant `networkGraph`
    // capability. When it is off the server's /api/graph/* routes 404, so the
    // tab + graph panel must hide and the default view must fall back to cards
    // (otherwise a desktop visitor lands on an empty graph).
    const graphEnabled = useCapability('networkGraph');

    // Maps legacy ?view= values onto the routes that now serve them:
    // ?view=ecosystem -> /ecosystem
    // ?view=partners  -> /connections/partners
    // ?view=mentors   -> /connections/mentors
    // Runs whenever searchParams changes and replaces the
    // history entry rather than pushing one. Any other value,
    // including the still-supported ?view=cards and
    // ?view=graph, falls through and is handled by this page.
    useEffect(() => {
        const view = searchParams.get('view');
        if (view === 'ecosystem') {
            navigate('/ecosystem', { replace: true });
        } else if (view === 'partners') {
            navigate('/connections/partners', { replace: true });
        } else if (view === 'mentors') {
            navigate('/connections/mentors', { replace: true });
        }
    }, [searchParams, navigate]);

    // Ecosystem-admin signal: `User.role === 'admin'` no longer exists
    // post-P4a; admin authority for per-tenant features comes from
    // EcosystemMembership.role in the active tenant. The admin-only
    // "Profile Completeness" (hasDescription) filter lights up for
    // ecosystem admins (review finding 9).
    const isAdmin = isMember && membershipRole === 'admin';

    // ---- View Mode ----
    // Determines which content panel is shown: 'cards' or 'graph'.
    // Default is graph on desktop, cards on mobile (graph requires large viewport).
    // URL ?view= param takes precedence if it's a valid view mode.
    const getDefaultView = () => {
        // Graph view is unavailable when networkGraph is disabled: force cards.
        if (!graphEnabled) return 'cards';
        const urlView = searchParams.get('view');
        if (urlView === 'cards' || urlView === 'graph') return urlView;
        return isMobile() ? 'cards' : 'graph';
    };

    const [viewMode, setViewMode] = useState(getDefaultView);

    // ---- Search and Filter State ----
    // Filters are initialized from URL search params so that shared links
    // restore the exact filter configuration.
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({
        sector: searchParams.get('sector') || '',
        region: searchParams.get('region') || '',
        programming: searchParams.get('programming') || '',
        hasWebsite: searchParams.get('hasWebsite') || '',
        hasDescription: searchParams.get('hasDescription') || '',
        minConnections: searchParams.get('minConnections') || '',
    });

    // ---- URL-to-State Sync Effects ----
    // When navigation occurs via a <Link> (e.g., from the ecosystem page clicking
    // a sector), the URL params change but local state hasn't updated yet.
    // These effects sync viewMode and sector filter from URL params.
    const urlView = searchParams.get('view');
    const urlSector = searchParams.get('sector') || '';

    // Sync view mode from URL -- also sets a flag to scroll to top after new data loads.
    // A bookmarked ?view=graph URL is ignored when networkGraph is disabled so
    // the user can't land on a graph the server won't serve.
    useEffect(() => {
        if (urlView === 'graph' && !graphEnabled) return;
        if (urlView && urlView !== viewMode) {
            setViewMode(urlView);
            scrollToTopAfterLoad.current = true;
        }
    }, [urlView, graphEnabled]); // eslint-disable-line

    // Sync sector filter from URL -- auto-expands advanced filters panel if a sector is set
    useEffect(() => {
        if (urlSector !== filters.sector) {
            setFilters((prev) => ({ ...prev, sector: urlSector }));
            if (urlSector) setShowAdvanced(true);
        }
    }, [urlSector]); // eslint-disable-line

    // ---- Sort State (card view only) ----
    const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'name');
    const [sortOrder, setSortOrder] = useState(
        searchParams.get('sortOrder') || 'asc'
    );

    // Determine if any advanced filters are active so we can auto-expand
    // the collapsible filter panel and show an "Active" badge
    const hasAdvancedFilters =
        searchParams.get('sector') ||
        searchParams.get('region') ||
        searchParams.get('programming') ||
        searchParams.get('hasWebsite') ||
        searchParams.get('hasDescription') ||
        searchParams.get('minConnections');

    const [showAdvanced, setShowAdvanced] = useState(!!hasAdvancedFilters);

    // Ref flag: when true, the next time loading finishes, the page scrolls
    // to the top. Used when the view mode changes via URL navigation to ensure
    // the user starts at the top of the new view.
    const scrollToTopAfterLoad = useRef(false);

    // ---- Card View Data State ----
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    // initialLoad distinguishes the first fetch (show full-page spinner) from
    // subsequent fetches (show inline spinner next to result count).
    const [initialLoad, setInitialLoad] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: ITEMS_PER_PAGE,
        total: 0,
        pages: 1,
    });

    // Publish page context for the floating AI chat widget so it can
    // tailor its responses based on what the user is currently viewing
    // (e.g., "I see you're browsing Non-profit organizations in Denver Metro").
    useChatPageEffect(
        {
            page: 'directory',
            viewMode,
            searchQuery: searchQuery || null,
            activeFilters: {
                sector: filters.sector || null,
                region: filters.region || null,
                programming: filters.programming || null,
            },
            resultCount: pagination.total,
            currentPage: pagination.page,
            visibleOrganizations:
                viewMode === 'cards'
                    ? organizations.map((org) => org.name)
                    : null,
        },
        [
            viewMode,
            searchQuery,
            filters.sector,
            filters.region,
            filters.programming,
            pagination.total,
            pagination.page,
            organizations,
        ]
    );

    // After data finishes loading, scroll to top if a URL-driven view change
    // set the scrollToTopAfterLoad flag. This ensures the user sees the top of
    // the new view rather than being stranded at the previous scroll position.
    useEffect(() => {
        if (!loading && scrollToTopAfterLoad.current) {
            scrollToTopAfterLoad.current = false;
            // Scroll both window and body/documentElement to cover all scroll containers
            window.scrollTo({ top: 0, behavior: 'instant' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        }
    }, [loading]);

    const orgApi = useOrganizationApi();
    // Store orgApi in a ref so fetchOrganizations (a useCallback) doesn't
    // re-create on every render when orgApi's identity changes. This prevents
    // unnecessary refetches while keeping the callback always using the
    // latest API instance.
    const orgApiRef = useRef(orgApi);
    orgApiRef.current = orgApi;

    // Memoize the graph's initial filter object so the EcosystemGraph component
    // doesn't re-render unless an actual filter value changes.
    const graphInitialFilters = useMemo(
        () => ({
            sector: filters.sector,
            region: filters.region,
            programming: filters.programming,
            searchTerm: searchQuery,
            hasWebsite: filters.hasWebsite,
            hasDescription: filters.hasDescription,
            minConnections: filters.minConnections,
        }),
        [
            filters.sector,
            filters.region,
            filters.programming,
            searchQuery,
            filters.hasWebsite,
            filters.hasDescription,
            filters.minConnections,
        ]
    );

    // Fetch organizations for card view.
    // Builds a params object from current filter/sort/page state and calls
    // either the search endpoint (when searchQuery >= 2 chars) or the standard
    // listing endpoint. Both endpoints return the same response shape with
    // { organizations, pagination }.
    const fetchOrganizations = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page: currentPage,
                limit: ITEMS_PER_PAGE,
                sortBy,
                sortOrder,
            };

            // Only include non-empty filter values in the API params
            if (filters.sector) params.sector = filters.sector;
            if (filters.region) params.region = filters.region;
            if (filters.programming) params.programming = filters.programming;
            if (filters.hasWebsite) params.hasWebsite = filters.hasWebsite;
            if (filters.hasDescription) {
                params.hasDescription = filters.hasDescription;
            }
            if (filters.minConnections) {
                params.minConnections = filters.minConnections;
            }

            let data;
            // Use the search endpoint for text queries, standard listing otherwise.
            // The search endpoint uses MongoDB text indexes for full-text matching.
            if (searchQuery && searchQuery.length >= 2) {
                data = await orgApiRef.current.searchOrganizations(
                    searchQuery,
                    params
                );
            } else {
                data = await orgApiRef.current.fetchOrganizations(params);
            }

            setOrganizations(data.organizations || []);
            if (data.pagination) {
                setPagination(data.pagination);
            }
        } catch (err) {
            setError('Failed to load organizations');
            console.error(err);
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    }, [
        currentPage,
        filters.sector,
        filters.region,
        filters.programming,
        filters.hasWebsite,
        filters.hasDescription,
        filters.minConnections,
        searchQuery,
        sortBy,
        sortOrder,
    ]);

    // Trigger data fetch whenever fetchOrganizations changes (i.e., when any
    // filter, sort, page, or search dependency changes) but only in card view.
    // In graph view, the EcosystemGraph component fetches its own data.
    useEffect(() => {
        if (viewMode === 'cards') {
            fetchOrganizations();
        } else {
            setInitialLoad(false);
            setLoading(false);
        }
    }, [fetchOrganizations, viewMode]);

    // Reset to page 1 whenever any filter, search, or sort value changes.
    // This prevents the user from being stranded on a page number that doesn't
    // exist in the new result set.
    useEffect(() => {
        setCurrentPage(1);
    }, [
        filters.sector,
        filters.region,
        filters.hasWebsite,
        filters.hasDescription,
        filters.minConnections,
        searchQuery,
        sortBy,
        sortOrder,
    ]);

    // Updates a single filter value in both local state and URL params.
    // Empty values remove the key from the URL to keep it clean.
    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        const newParams = new URLSearchParams(searchParams);
        if (value) {
            newParams.set(key, value);
        } else {
            newParams.delete(key);
        }
        setSearchParams(newParams);
        // Reveal the advanced filter panel whenever a filter is set (e.g. via a
        // card tag chip) so the now-active filter is visible even if the panel
        // was collapsed. Clearing a filter never force-opens the panel.
        if (value) {
            setShowAdvanced(true);
        }
    };

    // Switches between 'cards' and 'graph' view modes and persists in URL.
    const handleViewChange = (mode) => {
        setViewMode(mode);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('view', mode);
        setSearchParams(newParams);
    };

    // Resets all filters, search, and sort to defaults and clears the URL params.
    const handleClearFilters = () => {
        setSearchQuery('');
        setFilters({
            sector: '',
            region: '',
            programming: '',
            hasWebsite: '',
            hasDescription: '',
            minConnections: '',
        });
        setSortBy('name');
        setSortOrder('asc');
        setSearchParams({});
    };

    // Parses a combined "field-direction" string (e.g., "name-asc") from the
    // sort dropdown into separate sortBy and sortOrder values.
    const handleSortChange = (value) => {
        const [newSortBy, newSortOrder] = value.split('-');
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('sortBy', newSortBy);
        newParams.set('sortOrder', newSortOrder);
        setSearchParams(newParams);
    };

    // Builds a truncated pagination control with ellipsis.
    // Always shows page 1 and the last page, with a sliding window of
    // +/- 1 page around the current page. Ellipsis markers appear when
    // there are gaps between the first/last page and the window.
    const getPaginationItems = () => {
        const items = [];
        const { page, pages } = pagination;

        items.push(
            <Pagination.Item
                key={1}
                active={page === 1}
                onClick={() => setCurrentPage(1)}
            >
                1
            </Pagination.Item>
        );

        if (page > 3) {
            items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (
            let i = Math.max(2, page - 1);
            i <= Math.min(pages - 1, page + 1);
            i++
        ) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={page === i}
                    onClick={() => setCurrentPage(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (page < pages - 2) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        }

        if (pages > 1) {
            items.push(
                <Pagination.Item
                    key={pages}
                    active={page === pages}
                    onClick={() => setCurrentPage(pages)}
                >
                    {pages}
                </Pagination.Item>
            );
        }

        return items;
    };

    // Show spinner on initial load for card view
    if (initialLoad && loading && viewMode === 'cards') {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <>
            <SEO
                title="Ecosystem"
                description={`Browse and search STEM organizations across ${ecosystemName}. View as cards or explore the interactive network visualization.`}
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Ecosystem', path: '/directory' },
                ]}
            />
            <Container className="py-5">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                    <div>
                        <h1 className="text-primary mb-2">Ecosystem</h1>
                        <p className="lead mb-0">
                            Search and filter organizations by sector, region,
                            and/or program type to find potential partners and
                            resources.
                        </p>
                    </div>
                    {/* Member-only shareable join link, relocated here from
                        the Dashboard (#275) now that Dashboard lives at the
                        platform apex and no longer resolves an ecosystem. */}
                    <ShareInviteButton />
                </div>

                {/* View Toggle Tabs */}
                <Nav
                    variant="tabs"
                    activeKey={viewMode}
                    onSelect={(k) => handleViewChange(k)}
                    className="mb-4"
                >
                    <Nav.Item>
                        <Nav.Link eventKey="cards">
                            <span className="me-2">📋</span>
                            Card View
                        </Nav.Link>
                    </Nav.Item>
                    {graphEnabled && (
                        <Nav.Item>
                            <Nav.Link eventKey="graph">
                                <span className="me-2">🕸️</span>
                                Network Visualization
                            </Nav.Link>
                        </Nav.Item>
                    )}
                </Nav>

                {/* Search Form - only show for cards and graph views */}
                {(viewMode === 'cards' || viewMode === 'graph') && (
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="p-4">
                            <Form onSubmit={(e) => e.preventDefault()}>
                                {/* Main Search */}
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-medium">
                                        Search Organizations
                                    </Form.Label>
                                    <SearchAutocomplete
                                        value={searchQuery}
                                        onChange={setSearchQuery}
                                        placeholder="Search by name, description, or keywords..."
                                        size="lg"
                                        navigateOnSelect={true}
                                    />
                                </Form.Group>

                                {/* Advanced Filters Toggle */}
                                <Button
                                    variant="link"
                                    onClick={() =>
                                        setShowAdvanced(!showAdvanced)
                                    }
                                    aria-controls="advanced-filters"
                                    aria-expanded={showAdvanced}
                                    className="p-0 mb-3 text-decoration-none"
                                >
                                    {showAdvanced ? '▼' : '▶'} Advanced Filters
                                    {hasAdvancedFilters && (
                                        <Badge
                                            bg="primary"
                                            pill
                                            className="ms-2"
                                        >
                                            Active
                                        </Badge>
                                    )}
                                </Button>

                                {/* Collapsible Advanced Filters */}
                                <Collapse in={showAdvanced}>
                                    <div id="advanced-filters">
                                        <Row className="g-3">
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>
                                                        Sector
                                                    </Form.Label>
                                                    <Form.Select
                                                        value={filters.sector}
                                                        onChange={(e) =>
                                                            handleFilterChange(
                                                                'sector',
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            All Sectors
                                                        </option>
                                                        {taxonomySectors.map(
                                                            (sector) => (
                                                                <option
                                                                    key={sector}
                                                                    value={
                                                                        sector
                                                                    }
                                                                >
                                                                    {sector}
                                                                </option>
                                                            )
                                                        )}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>
                                                        Region
                                                    </Form.Label>
                                                    <Form.Select
                                                        value={filters.region}
                                                        onChange={(e) =>
                                                            handleFilterChange(
                                                                'region',
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            All Regions
                                                        </option>
                                                        {taxonomyRegions.map(
                                                            (region) => (
                                                                <option
                                                                    key={region}
                                                                    value={
                                                                        region
                                                                    }
                                                                >
                                                                    {region}
                                                                </option>
                                                            )
                                                        )}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>
                                                        Programming Types
                                                    </Form.Label>
                                                    <Form.Select
                                                        value={
                                                            filters.programming
                                                        }
                                                        onChange={(e) =>
                                                            handleFilterChange(
                                                                'programming',
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            All Program Types
                                                        </option>
                                                        {taxonomyProgrammingTypes.map(
                                                            (
                                                                programmingType
                                                            ) => (
                                                                <option
                                                                    key={
                                                                        programmingType
                                                                    }
                                                                    value={
                                                                        programmingType
                                                                    }
                                                                >
                                                                    {
                                                                        programmingType
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row className="g-3 mt-1">
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>
                                                        Website
                                                    </Form.Label>
                                                    <Form.Select
                                                        value={
                                                            filters.hasWebsite
                                                        }
                                                        onChange={(e) =>
                                                            handleFilterChange(
                                                                'hasWebsite',
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            Any
                                                        </option>
                                                        <option value="true">
                                                            Has Website
                                                        </option>
                                                        <option value="false">
                                                            No Website
                                                        </option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>
                                                        Min. Connections
                                                    </Form.Label>
                                                    <Form.Select
                                                        value={
                                                            filters.minConnections
                                                        }
                                                        onChange={(e) =>
                                                            handleFilterChange(
                                                                'minConnections',
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            Any
                                                        </option>
                                                        <option value="5">
                                                            5+
                                                        </option>
                                                        <option value="10">
                                                            10+
                                                        </option>
                                                        <option value="20">
                                                            20+
                                                        </option>
                                                        <option value="50">
                                                            50+
                                                        </option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            {/* Admin-only filter for profile completeness */}
                                            {isAdmin && (
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>
                                                            Profile Completeness
                                                            <Badge
                                                                bg="info"
                                                                className="ms-2"
                                                            >
                                                                Admin
                                                            </Badge>
                                                        </Form.Label>
                                                        <Form.Select
                                                            value={
                                                                filters.hasDescription
                                                            }
                                                            onChange={(e) =>
                                                                handleFilterChange(
                                                                    'hasDescription',
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                        >
                                                            <option value="">
                                                                Any Profile
                                                            </option>
                                                            <option value="true">
                                                                Has Description
                                                            </option>
                                                            <option value="false">
                                                                Missing
                                                                Description
                                                            </option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            )}
                                        </Row>
                                    </div>
                                </Collapse>

                                <div className="mt-4 d-grid d-lg-flex gap-2 justify-content-lg-between">
                                    <div className="d-grid d-lg-flex gap-2">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="rounded-pill px-4"
                                        >
                                            Search Ecosystem
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            className="rounded-pill"
                                            onClick={handleClearFilters}
                                        >
                                            Clear Filters
                                        </Button>
                                    </div>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                )}

                {/* Content based on view mode */}
                {viewMode === 'cards' && (
                    <>
                        {/* Card View */}
                        {error && <p className="text-danger">{error}</p>}

                        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3 gap-2">
                            <div className="d-flex align-items-center">
                                <p className="text-muted mb-0">
                                    Showing {organizations.length} of{' '}
                                    {pagination.total} organizations
                                    {pagination.pages > 1 &&
                                        ` (Page ${pagination.page})`}
                                </p>
                                {loading && !initialLoad && (
                                    <Spinner
                                        animation="border"
                                        variant="primary"
                                        size="sm"
                                        className="ms-2"
                                    />
                                )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Label className="mb-0 small text-muted">
                                    Sort by:
                                </Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={`${sortBy}-${sortOrder}`}
                                    onChange={(e) =>
                                        handleSortChange(e.target.value)
                                    }
                                    className="select-auto-width"
                                >
                                    <option value="name-asc">Name (A-Z)</option>
                                    <option value="name-desc">
                                        Name (Z-A)
                                    </option>
                                    <option value="connectionCount-desc">
                                        Most Connected
                                    </option>
                                    <option value="connectionCount-asc">
                                        Least Connected
                                    </option>
                                    <option value="createdAt-desc">
                                        Newest First
                                    </option>
                                    <option value="createdAt-asc">
                                        Oldest First
                                    </option>
                                </Form.Select>
                            </div>
                        </div>

                        <Row className="g-4">
                            {organizations.map((org) => {
                                // Tag chips derive from the org's own taxonomy
                                // values (sectors + programming types).
                                // These were validated against the active
                                // ecosystem's taxonomy on save, so they are
                                // always labels belonging to the current tenant.
                                // Each chip records which directory filter it
                                // maps to so a click filters the right field
                                // (sector vs. programming).
                                const tagChips = [
                                    ...[
                                        org.primarySector,
                                        ...(org.secondarySectors || []),
                                    ]
                                        .filter(Boolean)
                                        .map((value) => ({
                                            value,
                                            filterKey: 'sector',
                                        })),
                                    ...(org.programmingTypes || [])
                                        .filter(Boolean)
                                        .map((value) => ({
                                            value,
                                            filterKey: 'programming',
                                        })),
                                ];
                                // Show at most 2 chips; surface the rest behind
                                // a "+N more" label so cards with many tags
                                // don't overflow.
                                const visibleChips = tagChips.slice(0, 2);
                                const hiddenCount =
                                    tagChips.length - visibleChips.length;
                                return (
                                    <Col md={6} lg={4} key={org._id}>
                                        <Card className="h-100 border-0 shadow-sm org-card">
                                            <Card.Body className="p-4 d-flex flex-column">
                                                <img
                                                    src={
                                                        org.imageUrl ||
                                                        RILE_MARK_URL
                                                    }
                                                    alt={org.name}
                                                    className="org-image mb-3"
                                                    onError={(e) => {
                                                        e.target.src =
                                                            RILE_MARK_URL;
                                                    }}
                                                />
                                                <h5 className="text-primary">
                                                    {highlightText(
                                                        org.name,
                                                        searchQuery
                                                    )}
                                                </h5>
                                                {tagChips.length > 0 && (
                                                    <div className="tag-chips mb-2">
                                                        {visibleChips.map(
                                                            (chip) => (
                                                                <button
                                                                    key={`${chip.filterKey}:${chip.value}`}
                                                                    type="button"
                                                                    className="tag-chip"
                                                                    aria-label={`Filter directory by ${chip.value}`}
                                                                    onClick={() =>
                                                                        handleFilterChange(
                                                                            chip.filterKey,
                                                                            chip.value
                                                                        )
                                                                    }
                                                                >
                                                                    #
                                                                    {chip.value}
                                                                </button>
                                                            )
                                                        )}
                                                        {hiddenCount > 0 && (
                                                            <span className="tag-chip-more">
                                                                +{hiddenCount}{' '}
                                                                more
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                <p className="text-muted small mb-3">
                                                    {highlightAndTruncate(
                                                        org.description,
                                                        searchQuery,
                                                        120
                                                    )}
                                                </p>
                                                {org.regionsServed?.length >
                                                    0 && (
                                                    <p className="small mb-2">
                                                        <strong>
                                                            Regions:
                                                        </strong>{' '}
                                                        {org.regionsServed
                                                            .slice(0, 3)
                                                            .join(', ')}
                                                    </p>
                                                )}
                                                {/* Connection count */}
                                                {org.connectionCount > 0 && (
                                                    <p className="small text-muted mb-0">
                                                        🔗 {org.connectionCount}{' '}
                                                        connection
                                                        {org.connectionCount !==
                                                            1 && 's'}
                                                    </p>
                                                )}
                                                {/* Action buttons - pushed to bottom */}
                                                <div className="d-grid gap-2 mt-auto pt-3">
                                                    <Button
                                                        as={Link}
                                                        to={`/organizations/${org._id}`}
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="rounded-pill"
                                                    >
                                                        View Details
                                                    </Button>
                                                    {org.website && (
                                                        <Button
                                                            href={org.website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="rounded-pill"
                                                        >
                                                            Visit Website
                                                        </Button>
                                                    )}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>

                        {organizations.length === 0 && !loading && (
                            <div className="text-center py-5">
                                <p className="text-muted">
                                    No organizations found matching your
                                    criteria.
                                </p>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div className="d-flex justify-content-center mt-4">
                                <Pagination>
                                    <Pagination.Prev
                                        disabled={pagination.page === 1}
                                        onClick={() =>
                                            setCurrentPage((p) =>
                                                Math.max(1, p - 1)
                                            )
                                        }
                                    />
                                    {getPaginationItems()}
                                    <Pagination.Next
                                        disabled={
                                            pagination.page === pagination.pages
                                        }
                                        onClick={() =>
                                            setCurrentPage((p) =>
                                                Math.min(
                                                    pagination.pages,
                                                    p + 1
                                                )
                                            )
                                        }
                                    />
                                </Pagination>
                            </div>
                        )}
                    </>
                )}
                {graphEnabled && viewMode === 'graph' && (
                    <>
                        {/* Graph View - Desktop */}
                        <div className="d-none d-lg-block">
                            <EcosystemGraph
                                initialFilters={graphInitialFilters}
                                height={600}
                                showControls={true}
                                showLegend={true}
                            />
                        </div>

                        {/* Graph View - Mobile (stats + list) */}
                        <div className="d-lg-none">
                            <GraphMobileView
                                filters={{
                                    searchTerm: searchQuery,
                                    sector: filters.sector,
                                    region: filters.region,
                                    programming: filters.programming,
                                    hasWebsite: filters.hasWebsite,
                                    hasDescription: filters.hasDescription,
                                    minConnections: filters.minConnections,
                                }}
                            />
                        </div>
                    </>
                )}
            </Container>
        </>
    );
}
